import { AlignLeft, List, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import type { WorkspaceFilter } from '../utils/workspaceFilter';

/**
 * How the Q&A index lays its rows out. `aligned` pins each answered row to the
 * vertical position of its answer in the document (comment-rail style);
 * `compact` is the dense, evenly-spaced, independently-scrolling list.
 */
export type WorkspaceViewMode = 'aligned' | 'compact';

export interface WorkspaceFilterBarProps {
  filter: WorkspaceFilter;
  search: string;
  onFilterChange: (filter: WorkspaceFilter) => void;
  onSearchChange: (search: string) => void;
  counts: Record<WorkspaceFilter, number>;
  /** The active row layout mode. */
  viewMode: WorkspaceViewMode;
  /** Switch the row layout between aligned and compact. */
  onViewModeChange: (mode: WorkspaceViewMode) => void;
  className?: string;
}

const PILLS: { value: WorkspaceFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'approved', label: 'Approved' },
];

const VIEW_MODES: { value: WorkspaceViewMode; label: string; Icon: typeof AlignLeft }[] = [
  { value: 'aligned', label: 'Aligned', Icon: AlignLeft },
  { value: 'compact', label: 'Compact', Icon: List },
];

/**
 * The Q&A index controls: three filter pills (All / Needs review / Approved)
 * and a search box. The active pill is styled as a solid chip; the others read
 * as quiet outlines. Counts are rendered inline so the reviewer sees the size
 * of each bucket before switching.
 */
export function WorkspaceFilterBar({
  filter,
  search,
  onFilterChange,
  onSearchChange,
  counts,
  viewMode,
  onViewModeChange,
  className,
}: WorkspaceFilterBarProps) {
  return (
    <div className={cn('space-y-2.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <div role="group" aria-label="Filter questions" className="flex items-center gap-1.5">
          {PILLS.map((pill) => {
            const isActive = filter === pill.value;
            return (
              <button
                key={pill.value}
                type="button"
                aria-pressed={isActive}
                onClick={() => onFilterChange(pill.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-foreground text-background'
                    : 'border border-border text-muted-foreground hover:bg-muted/50',
                )}
              >
                <span>{pill.label}</span>
                <span
                  className={cn(
                    'font-mono text-[10px]',
                    isActive ? 'text-background/70' : 'text-muted-foreground/70',
                  )}
                >
                  {counts[pill.value]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Segmented Aligned / Compact toggle — switches the row layout. */}
        <div
          role="group"
          aria-label="Row layout"
          className="inline-flex items-center rounded-full border border-border p-0.5"
        >
          {VIEW_MODES.map(({ value, label, Icon }) => {
            const isActive = viewMode === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={isActive}
                onClick={() => onViewModeChange(value)}
                title={`${label} layout`}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-muted/50',
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          aria-label="Search questions"
          placeholder="Search questions…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>
    </div>
  );
}
